import axios from 'axios';
import { toast } from 'react-toastify';
import { ENDPOINTS } from './config';
import { mockInfluencers, mockImages, mockTrainingSettings } from './mockData';

// Get API URL from environment
const API_URL = process.env.REACT_APP_API_URL;

// Check if we're in local development
const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Check if we're using CloudFront
const isCloudFront = window.location.hostname.includes('cloudfront.net');
const isPipelineOrForm = window.location.hostname.includes('pipeline.oh.xyz') || 
                         window.location.hostname.includes('form.oh.xyz');

// For local dev, use proxy by using relative URLs
const apiBaseURL = isLocalDev ? '/prod' : API_URL;

// Log API URL for debugging
console.log(`API URL: ${apiBaseURL} (isLocalDev: ${isLocalDev})`);

// Create axios instance with base URL
const api = axios.create({
  baseURL: apiBaseURL,
  headers: {
    'Content-Type': 'application/json'
  },
  // Don't use withCredentials for local development with proxy
  withCredentials: !isLocalDev
});

// Request interceptor for adding auth token and logging requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Handle FormData requests - don't set Content-Type for FormData
    if (config.data instanceof FormData) {
      // Remove Content-Type header for FormData - let browser set it with boundary
      delete config.headers['Content-Type'];
    }
    
    // Enhanced logging for debugging - show full request details
    console.group(`🚀 API Request: ${config.method.toUpperCase()} ${config.baseURL}${config.url}`);
    console.log('Headers:', config.headers);
    
    // Log request body for influencer operations
    if (config.url.includes('/api/influencers')) {
      console.log('🔍 INFLUENCER API REQUEST DATA:', JSON.stringify(config.data, null, 2));
      
      // Specifically highlight the 5 new fields
      if (config.data) {
        const newFields = ['reference_image', 'prompt', 'nudity_level', 'prompt_prefix', 'prompt_suffix'];
        console.log('📋 NEW FIELDS BEING SENT:');
        newFields.forEach(field => {
          if (config.data[field] !== undefined) {
            console.log(`  ✅ ${field}: ${JSON.stringify(config.data[field])}`);
          } else {
            console.log(`  ❌ ${field}: NOT PROVIDED`);
          }
        });
      }
    } else {
      console.log('Request Data:', config.data);
    }
    console.groupEnd();
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

// Response interceptor for handling errors and logging responses
api.interceptors.response.use(
  (response) => {
    // Enhanced logging for debugging - show full response details
    console.group(`✅ API Response (${response.status}): ${response.config.method.toUpperCase()} ${response.config.url}`);
    
    // Log response data for influencer operations
    if (response.config.url.includes('/api/influencers')) {
      console.log('🔍 INFLUENCER API RESPONSE DATA:', JSON.stringify(response.data, null, 2));
      
      // Specifically highlight the 5 new fields in response
      if (response.data) {
        const newFields = ['reference_image', 'prompt', 'nudity_level', 'prompt_prefix', 'prompt_suffix'];
        console.log('📋 NEW FIELDS IN RESPONSE:');
        newFields.forEach(field => {
          if (response.data[field] !== undefined && response.data[field] !== null) {
            console.log(`  ✅ ${field}: ${JSON.stringify(response.data[field])}`);
          } else {
            console.log(`  ❌ ${field}: NULL/MISSING`);
          }
        });
      }
    } else {
      console.log('Response Data:', response.data);
    }
    console.groupEnd();
    
    return response;
  },
  (error) => {
    const { response } = error;
    
    // Enhanced error logging
    console.group(`❌ API Error (${response?.status || 'No Status'}): ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
    if (response) {
      console.error('Error Data:', response.data);
      console.error('Error Headers:', response.headers);
      
      // Special handling for influencer API errors
      if (error.config?.url?.includes('/api/influencers')) {
        console.error('🚨 INFLUENCER API ERROR DETAILS:', {
          status: response.status,
          statusText: response.statusText,
          data: response.data,
          requestData: error.config?.data
        });
      }
    } else {
      console.error('Network Error:', error.message);
    }
    console.groupEnd();
    
    if (response && response.status === 401) {
      // In development mode, don't redirect to login or show error
      if (isDevelopment) {
        console.warn('401 Unauthorized error in development mode. Not redirecting to login.');
        
        // In development mode, try to create a new token automatically
        const mockToken = `local-auth-token-${Date.now()}`;
        
        // Create mock user data
        const userData = {
          email: 'admin@example.com',
          name: 'Admin User',
          sub: `local-${Date.now()}`,
          username: 'admin@example.com'
        };
        
        // Store in localStorage for persistence
        localStorage.setItem('localAuthToken', mockToken);
        localStorage.setItem('localUserData', JSON.stringify(userData));
        localStorage.setItem('token', mockToken);
        
        // Set up axios auth header with the new token
        api.defaults.headers.common['Authorization'] = `Bearer ${mockToken}`;
        
        console.log('Created new development token. Please try your request again.');
        
        // Don't remove the token in development mode
      } else {
        // Unauthorized - clear token and redirect to login
        localStorage.removeItem('token');
        window.location.href = '/login';
        toast.error('Session expired. Please log in again.');
      }
    } else if (response && response.data && response.data.message) {
      // Show error message from API
      toast.error(response.data.message);
    } else {
      // Generic error message
      toast.error('An error occurred. Please try again.');
    }
    
    return Promise.reject(error);
  }
);

// Authentication API calls
export const authAPI = {
  login: async (tokenId) => {
    const response = await api.post(ENDPOINTS.AUTH.LOGIN, { tokenId });
    return response.data;
  },
  logout: () => {
    localStorage.removeItem('token');
  },
};

// Influencer API calls
export const influencerAPI = {
  getAll: async () => {
    try {
      const response = await api.get(ENDPOINTS.INFLUENCERS.BASE);
      // Ensure the response is an array
      if (Array.isArray(response.data)) {
        return response.data;
      } else {
        console.warn(`Expected array response from ${ENDPOINTS.INFLUENCERS.BASE} but got:`, response.data);
        // If response.data is not an array, return an empty array
        return [];
      }
    } catch (error) {
      console.error('Error fetching influencers:', error);
      // Return an empty array on error to prevent frontend errors
      return [];
    }
  },
  getById: async (id) => {
    const response = await api.get(ENDPOINTS.INFLUENCERS.BY_ID(id));
    return response.data;
  },
  create: async (influencerData) => {
    const response = await api.post(ENDPOINTS.INFLUENCERS.BASE, influencerData);
    return response.data;
  },
  update: async (id, influencerData) => {
    const response = await api.put(ENDPOINTS.INFLUENCERS.BY_ID(id), influencerData);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(ENDPOINTS.INFLUENCERS.BY_ID(id));
    return response.data;
  },
  generateHash: async (id, daysValid = 30) => {
    const response = await api.post(`${ENDPOINTS.INFLUENCERS.BY_ID(id)}/generate-hash`, { days_valid: daysValid });
    return response.data;
  },
};

// Images API calls
export const imagesAPI = {
  getByFolder: async (folder) => {
    const response = await api.get(ENDPOINTS.IMAGES.BY_FOLDER(folder));
    return response.data;
  },
  moveImage: async (imageId, fromFolder, toFolder) => {
    const response = await api.post(ENDPOINTS.IMAGES.TRANSITION, {
      imageId,
      fromFolder,
      toFolder,
    });
    return response.data;
  },
  deleteImage: async (folder, filename) => {
    const response = await api.delete(`${ENDPOINTS.IMAGES.BASE}/${folder}/${filename}`);
    return response.data;
  },
  registerImages: async (images, verify_s3 = false) => {
    // Use the direct Lambda URL for registration endpoint (no /production prefix)
    const registerUrl = 'https://aulgvbsj7dsbfd43s3gmqcm52m0dcorn.lambda-url.us-east-1.on.aws/api/images/register';
    
    console.log('📝 [Image Registration] Starting registration for', images.length, 'images');
    console.log('📝 [Image Registration] Verify S3:', verify_s3);
    console.log('📝 [Image Registration] Sample image:', images[0]);
    
    // Get auth token for uploaded_by field
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(
        registerUrl, 
        {
          images: images,
          verify_s3: verify_s3
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : undefined
          },
          timeout: 30000 // 30 second timeout
        }
      );
      
      console.log('✅ [Image Registration] Success! Response:', response.data);
      console.log('✅ [Image Registration] Registered count:', response.data?.registered?.length || 0);
      console.log('✅ [Image Registration] Updated count:', response.data?.updated?.length || 0);
      
      return response.data;
    } catch (error) {
      console.error('❌ [Image Registration] Failed:', error.message);
      console.error('❌ [Image Registration] Error details:', error.response?.data);
      throw error;
    }
  },
};

// Training settings API calls
export const trainingAPI = {
  getSettings: async () => {
    const response = await api.get(ENDPOINTS.TRAINING.SETTINGS);
    return response.data;
  },
  updateSettings: async (settings) => {
    const response = await api.put(ENDPOINTS.TRAINING.SETTINGS, settings);
    return response.data;
  },
  startTraining: async () => {
    const response = await api.post(ENDPOINTS.TRAINING.START);
    return response.data;
  },
};

// Vintage classification API calls
export const vintageAPI = {
  // Apply vintage filter to images
  applyVintageFilter: async (influencerId, imageIds, vintageSettings = {}) => {
    const response = await api.post('/api/filter/vintage', {
      influencer_id: influencerId,
      image_ids: imageIds,
      vintage_settings: vintageSettings
    });
    return response.data;
  },
  
  // Get vintage settings for an influencer
  getVintageSettings: async (influencerId) => {
    const response = await api.get(`/api/filter/vintage/settings/${influencerId}`);
    return response.data;
  },
  
  // Update vintage settings for an influencer
  updateVintageSettings: async (influencerId, settings) => {
    const response = await api.put(`/api/filter/vintage/settings/${influencerId}`, settings);
    return response.data;
  },
  
  // Get vintage status for an influencer
  getVintageStatus: async (influencerId) => {
    const response = await api.get(`/api/influencers/${influencerId}/vintage-status`);
    return response.data;
  }
};

// Image processing API calls (RunPod services)
export const processingAPI = {
  // Trigger cropping for a single image using S3CropRequest format
  cropImage: async (s3Bucket, s3Key, outputBucket, outputKey, prompt = 'female', requestId = null) => {
    const payload = {
      prompt: prompt, // 'male' or 'female' - used as the sex field
      s3_bucket: s3Bucket,
      s3_key: s3Key,
      output_bucket: outputBucket,
      output_key: outputKey
    };
    
    if (requestId) {
      payload.request_id = requestId;
    }
    
    const response = await api.post('/api/processing/crop', payload);
    return response.data;
  },
  
  // Trigger cropping for multiple images
  cropImages: async (influencerId, images, sex = 'female') => {
    const jobs = images.map(image => ({
      influencer_id: influencerId,
      s3_bucket: image.s3Bucket,
      s3_key: image.s3Key,
      output_bucket: image.s3Bucket,
      output_key: `${influencerId}/cropped/cropped_${image.filename}`,
      prompt: sex
    }));
    
    const response = await api.post('/api/processing/crop-batch', {
      jobs: jobs,
      influencer_id: influencerId
    });
    return response.data;
  },
  
  // Trigger captioning for a single image using S3CaptioningRequest format
  captionImage: async (s3Bucket, s3Key, outputBucket, outputKey, requestId = null) => {
    const payload = {
      s3_bucket: s3Bucket,
      s3_key: s3Key,
      output_bucket: outputBucket,
      output_key: outputKey
    };
    
    if (requestId) {
      payload.request_id = requestId;
    }
    
    const response = await api.post('/api/processing/caption', payload);
    return response.data;
  },
  
  // Trigger captioning for multiple cropped images
  captionImages: async (influencerId, images = null) => {
    const payload = {
      influencer_id: influencerId
    };
    
    if (images && images.length > 0) {
      payload.jobs = images.map(image => ({
        s3_bucket: image.s3Bucket,
        s3_key: image.s3Key,
        output_bucket: image.s3Bucket,
        output_key: image.s3Key.replace(/\.(jpg|jpeg|png)$/i, '.txt')
      }));
    }
    
    const response = await api.post('/api/processing/caption-batch', payload);
    return response.data;
  },
  
  // Get processing job status
  getJobStatus: async (jobId) => {
    const response = await api.get(`/api/processing/status/${jobId}`);
    return response.data;
  }
};

export default api;
